/**
 * Shared Institute Categories & Sub-categories Configuration & Helpers
 * ──────────────────────────────────────────────────────────────────────
 * Provides core archetypes (School, College, Coaching Institute) with
 * nested sub-categories, plus helpers for dynamic hierarchy building.
 */

export const DEFAULT_INSTITUTE_CATEGORIES = [
  {
    id: 'school',
    name: 'School',
    slug: 'school',
    icon: 'BookOpen',
    color: '#4F46E5',
    description: 'K-12 schools, primary, secondary and senior secondary education',
    subcategories: [
      { id: 'k12', name: 'K-12 School', slug: 'k12' },
      { id: 'preschool', name: 'Play School / Preschool', slug: 'preschool' },
      { id: 'primary', name: 'Primary School (Class 1-5)', slug: 'primary' },
      { id: 'secondary', name: 'Secondary / High School (Class 6-10)', slug: 'secondary' },
      { id: 'senior_secondary', name: 'Senior Secondary (Class 11-12)', slug: 'senior_secondary' },
    ],
  },
  {
    id: 'college',
    name: 'College',
    slug: 'college',
    icon: 'GraduationCap',
    color: '#2563EB',
    description: 'Colleges, universities and higher degree education',
    subcategories: [
      { id: 'degree_college', name: 'Degree College / University', slug: 'degree_college' },
      { id: 'engineering', name: 'Engineering & Technology', slug: 'engineering' },
      { id: 'management', name: 'Management & Commerce', slug: 'management' },
      { id: 'medical', name: 'Medical & Pharmacy', slug: 'medical' },
      { id: 'polytechnic', name: 'Polytechnic / Diploma', slug: 'polytechnic' },
    ],
  },
  {
    id: 'coaching_institute',
    name: 'Coaching Institute',
    slug: 'coaching_institute',
    icon: 'Target',
    color: '#7C3AED',
    description: 'IIT-JEE, NEET, competitive exams and tuition coaching',
    subcategories: [
      { id: 'jee', name: 'IIT-JEE / Engineering Entrance', slug: 'jee' },
      { id: 'neet', name: 'NEET / Medical Entrance', slug: 'neet' },
      { id: 'tuition', name: 'Foundation & School Tuitions', slug: 'tuition' },
      { id: 'govt_exams', name: 'UPSC / SSC / Govt Exams', slug: 'govt_exams' },
      { id: 'skill_development', name: 'Skill Development & IT Courses', slug: 'skill_development' },
      { id: 'commerce', name: 'Commerce & CA Foundation', slug: 'commerce' },
    ],
  },
];

/**
 * Generate flat list compatible with GeneralTab and Super Admin categories table
 */
export const DEFAULT_FLAT_CATEGORIES = DEFAULT_INSTITUTE_CATEGORIES.flatMap((root, rootIndex) => [
  {
    id: root.id,
    name: root.name,
    slug: root.slug,
    icon: root.icon,
    color: root.color,
    description: root.description,
    parent_id: null,
    display_order: rootIndex,
    is_active: true,
  },
  ...root.subcategories.map((sub, subIndex) => ({
    id: `${root.id}_${sub.slug || sub.id}`,
    name: sub.name,
    slug: sub.slug || sub.id,
    parent_id: root.id,
    display_order: subIndex,
    is_active: true,
  })),
]);

/**
 * Normalizes input string to standard primary category name.
 */
export function normalizeCategoryName(raw) {
  if (!raw) return 'School';
  const val = String(raw).trim().toLowerCase();
  if (val.includes('school')) return 'School';
  if (val.includes('college') || val.includes('university')) return 'College';
  if (val.includes('coaching') || val.includes('competitive') || val.includes('prep') || val.includes('tuition')) {
    return 'Coaching Institute';
  }
  return raw;
}

/**
 * Build a category tree (roots with nested subcategories) from a flat list
 * (such as what GET /api/auth/categories or GET /api/admin/categories returns).
 */
export function buildCategoryTree(flatList = []) {
  if (!Array.isArray(flatList) || flatList.length === 0) {
    return DEFAULT_INSTITUTE_CATEGORIES;
  }

  const active = flatList.filter(item => item.is_active !== false);
  const rootItems = active.filter(item => !item.parent_id);

  if (rootItems.length === 0) {
    return DEFAULT_INSTITUTE_CATEGORIES;
  }

  const tree = rootItems.map(root => {
    const children = active.filter(item => item.parent_id === root.id);
    return {
      id: root.id,
      name: root.name,
      slug: root.slug || root.name.toLowerCase().replace(/\s+/g, '_'),
      icon: root.icon || 'BookOpen',
      color: root.color || '#4F46E5',
      description: root.description || '',
      subcategories: children.map(c => ({
        id: c.id,
        name: c.name,
        slug: c.slug || c.name.toLowerCase().replace(/\s+/g, '_'),
        description: c.description || '',
      })),
    };
  });

  // Ensure the 3 core categories (School, College, Coaching Institute) are present in the tree
  const hasSchool = tree.some(c => matchCategoryRoot([c], 'school'));
  const hasCollege = tree.some(c => matchCategoryRoot([c], 'college'));
  const hasCoaching = tree.some(c => matchCategoryRoot([c], 'coaching institute'));

  const result = [...tree];
  if (!hasSchool) {
    result.unshift(DEFAULT_INSTITUTE_CATEGORIES[0]);
  }
  if (!hasCollege) {
    const schoolIdx = result.findIndex(c => matchCategoryRoot([c], 'school'));
    result.splice(schoolIdx + 1, 0, DEFAULT_INSTITUTE_CATEGORIES[1]);
  }
  if (!hasCoaching) {
    const collegeIdx = result.findIndex(c => matchCategoryRoot([c], 'college'));
    result.splice(collegeIdx + 1, 0, DEFAULT_INSTITUTE_CATEGORIES[2]);
  }

  return result;
}

/**
 * Match a root category by name, slug, or known alias
 */
export function matchCategoryRoot(categories = [], target = '') {
  if (!target || !categories.length) return null;
  const t = String(target).trim().toLowerCase();

  return categories.find(cat => {
    const n = String(cat.name || '').trim().toLowerCase();
    const s = String(cat.slug || '').trim().toLowerCase();
    if (n === t || s === t) return true;
    if (t.includes('school') && (n.includes('school') || s.includes('school'))) return true;
    if ((t.includes('college') || t.includes('university')) && (n.includes('college') || s.includes('college'))) return true;
    if ((t.includes('coaching') || t.includes('competitive')) && (n.includes('coaching') || s.includes('coaching') || s === 'competitive_exams')) return true;
    return false;
  }) || null;
}
